export const deduplicateMessages = (messagesArray: any[]): any[] => {
  const seen = new Set<string>();
  return messagesArray.filter((msg) => {
    const key = msg.id || msg.tempId;
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const normalizeMessage = (msg: any, currentUserId?: string | null) => {
  const text = msg?.content ?? msg?.text ?? "";
  const senderId = msg?.senderId ?? msg?.sender?.id;
  const isIncoming =
    msg?.isIncoming !== undefined
      ? msg.isIncoming
      : senderId && currentUserId
        ? String(senderId) !== String(currentUserId)
        : false;

  // Ensure reaction, metadata fields, and replyTo are preserved
  // Handle naming variation: backend sometimes sends 'allReactions' in broadcasts
  let rawReactions = msg?.reactions || msg?.allReactions || [];
  
  // Robustly handle stringified reactions if they arrive from backend as a JSON string
  if (typeof rawReactions === "string") {
    try {
      rawReactions = JSON.parse(rawReactions);
    } catch (e) {
      rawReactions = [];
    }
  }
  
  const reactions = Array.isArray(rawReactions) ? rawReactions : [];

  const reaction =
    typeof msg?.reaction === "string" ? msg.reaction :
    (reactions.length > 0 
      ? (typeof reactions[reactions.length - 1] === 'string' 
          ? reactions[reactions.length - 1] 
          : (reactions[reactions.length - 1]?.reaction || reactions[reactions.length - 1]?.emoji)) 
      : null);
  const isPinned = msg?.isPinned || false;
  const isArchived = msg?.isArchived || false;
  const isImportant = msg?.isImportant || false;
  const label = msg?.label || null;

  // Normalize replyTo to ensure it has a 'text' field and prefers decrypted content
  let replyTo = msg?.replyTo || null;
  if (replyTo) {
    let replyText = replyTo.content ?? replyTo.text ?? "";
    const looksHex = /^[0-9a-f]+$/i.test(replyText || "");
    const looksBase64 = /^[A-Za-z0-9+/=]+$/.test(replyText || "");
    if (replyText && replyText.length > 30 && (looksHex || looksBase64)) {
      replyText = "[Encrypted message]";
    }

    replyTo = {
      ...replyTo,
      text: replyText,
      content: replyTo.content || replyText,
    };
  }

  // Map messageType to type for consistency
  let type =
    msg?.type ||
    (msg?.messageType === "AUDIO" ||
      msg?.messageType === "VOICE" ||
      msg?.messageType === "voice"
      ? "voice"
      : null);

  // Parse productData if it's a string, otherwise use as-is
  let productData = msg?.productData || null;
  if (typeof productData === "string") {
    try {
      productData = JSON.parse(productData);
    } catch (e) {
      console.warn("Failed to parse productData string", e);
    }
  }

  const isProductInquiry = !!productData;

  // Extract status from statuses array if present
  const latestStatus =
    msg?.statuses && msg.statuses.length > 0
      ? msg.statuses[msg.statuses.length - 1].status
      : (isIncoming ? 'delivered' : (msg?.status || "sent"));

  let finalStatus = latestStatus;

  if (!isIncoming) {
    // If message has read status indicators, prioritize them
    if (msg?.isRead || msg?.readAt) {
      finalStatus = 'read';
    } else if (latestStatus === 'sending' && msg?.status && msg.status !== 'sending') {
      // Use the message's direct status if it's not "sending" and latestStatus is "sending"
      finalStatus = msg.status;
    } else if (latestStatus === 'sending' && !msg?.tempId) {
      // If message is not temporary (has real ID), don't keep it as "sending"
      finalStatus = 'sent';
    }
  }

  // Extract duration and waveformData which might be inside productData (seen in user example)
  const duration =
    msg?.duration ??
    productData?._voiceDuration ??
    productData?.duration ??
    null;
  const waveformData =
    msg?.waveformData ??
    productData?._waveformData ??
    productData?.waveformData ??
    null;

  // Convert individual file fields to files array for consistent handling
  let files: any[] = msg?.files || [];

  // If no files array, try to extract from productData._files
  if (files.length === 0 && msg?.productData?._files && Array.isArray(msg.productData._files)) {
    files = msg.productData._files;
  }

  // If still no files, convert individual field to files array
  if (files.length === 0 && msg?.fileUrl) {
    // Backend stores individual file fields, convert to files array
    files = [{
      fileUrl: msg.fileUrl,
      url: msg.fileUrl,
      fileName: msg.fileName || 'file',
      name: msg.fileName || 'file',
      fileType: msg.fileType || 'application/octet-stream',
      type: msg.fileType || 'application/octet-stream',
      fileSize: msg.fileSize || 0,
      size: msg.fileSize || 0
    }];
  }

  // Derive audioUrl from files array for voice messages
  const audioUrl = msg?.audioUrl ||
    (msg?.fileUrl) ||
    (type === 'voice' && files.length > 0 && files[0]?.fileType === 'audio/webm' ? files[0].fileUrl : null) ||
    null;

  return {
    ...msg,
    text,
    type,
    status: finalStatus,
    isIncoming,
    reaction,
    reactions,
    isPinned,
    isArchived,
    isImportant,
    label,
    replyTo,
    productData,
    isProductInquiry,
    duration,
    waveformData,
    audioUrl,
    files,
    timeString: msg.createdAt
      ? new Date(msg.createdAt).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
      : msg.timeString,
  };
};

export const getReplySenderLabel = (
  sender: any,
  currentUserId?: string | null,
) => {
  if (!sender) {
    return "User";
  }
  if (typeof sender === "string") {
    return sender;
  }
  if (
    sender.id &&
    currentUserId &&
    String(sender.id) === String(currentUserId)
  ) {
    return "You";
  }
  const first = sender.firstName || "";
  const last = sender.lastName || "";
  const name = `${first} ${last}`.trim();
  if (name) return name;
  return "User";
};
